import React, { useState, useEffect } from 'react';
import { fetchAllBills, deleteBill, updateBill, addPaymentToBill } from '../../../../services/api';

const BillsTable = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingBill, setEditingBill] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);


  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchAllBills();
      setBills(response);
    } catch (err) {
      setError(err.message || 'An error occurred while fetching bills');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBill = async (billId) => {
    try {
      await deleteBill(billId);
      setBills(bills.filter(bill => bill._id !== billId));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.message || 'Failed to delete bill');
    }
  };

  const handleUpdateBill = async (billId, updateData) => {
    try {
      const updatedBill = await updateBill(billId, updateData);
      setBills(bills.map(bill =>
        bill._id === billId ? updatedBill : bill
      ));
      setEditingBill(null);
    } catch (err) {
      setError(err.message || 'Failed to update bill');
    }
  };

  const handleAddPayment = async (billId, paymentData) => {
    try {
      await addPaymentToBill(billId, paymentData);
      // Refresh bills to show updated payment status
      await fetchBills();
      setPaymentModal(null);
    } catch (err) {
      setError(err.message || 'Failed to add payment');
    }
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateAdditionalTotal = (additional) => {
    return additional.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  // Filter bills based on selected date and search term
  const filteredBills = bills.filter(bill => {
    const matchesDate = !selectedDate ||
      new Date(bill.date).toDateString() === new Date(selectedDate).toDateString();

    const matchesSearch = !searchTerm ||
      (bill.clientName && bill.clientName.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesDate && matchesSearch;
  });

  // Calculate total summary
  const totalBillAmount = filteredBills.reduce((sum, bill) =>
    sum + (bill.totalBillAmount || 0), 0
  );

  // Group bills by date
  const groupedBills = filteredBills.reduce((groups, bill) => {
    const date = new Date(bill.date).toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(bill);
    return groups;
  }, {});

  const handleDateFilter = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    setSelectedDate('');
    setSearchTerm('');
  };

  // Edit Modal Component
  const EditModal = ({ bill, onClose, onSave }) => {
    const [editData, setEditData] = useState({
      clientName: bill.clientName || '',
      date: bill.date ? new Date(bill.date).toISOString().split('T')[0] : '',
      drillFeet: bill.drillFeet || 0,
      drillRate: bill.drillRate || 0,
      additional: bill.additional || []
    });

    const handleAdditionalChange = (index, field, value) => {
      const updatedItems = [...editData.additional];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        total: field === 'quantity' || field === 'rate'
          ? (field === 'quantity' ? value : updatedItems[index].quantity || 0) *
          (field === 'rate' ? value : updatedItems[index].rate || 0)
          : updatedItems[index].total
      };
      setEditData({ ...editData, additional: updatedItems });
    };

    const addNewItem = () => {
      setEditData({
        ...editData,
        additional: [...editData.additional, { name: '', quantity: 0, rate: 0, total: 0 }]
      });
    };

    const removeItem = (index) => {
      const updatedItems = editData.additional.filter((_, i) => i !== index);
      setEditData({ ...editData, additional: updatedItems });
    };

    const calculateTotalDrillAmount = () => {
      return editData.drillFeet * editData.drillRate;
    };

    const calculateTotalBillAmount = () => {
      const drillAmount = calculateTotalDrillAmount();
      const additionalAmount = calculateAdditionalTotal(editData.additional);
      return drillAmount + additionalAmount;
    };

    const handleSave = () => {
      const totalDrillAmount = calculateTotalDrillAmount();
      const totalBillAmount = calculateTotalBillAmount();

      const updatedData = {
        ...editData,
        totalDrillAmount,
        totalBillAmount
      };

      onSave(bill._id, updatedData);
    };



    

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Edit Bill</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  value={editData.clientName}
                  onChange={(e) => setEditData({ ...editData, clientName: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={editData.date}
                  onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>

            {/* Drilling Information */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-md font-medium text-blue-800 mb-3">Drilling Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drill Feet</label>
                  <input
                    type="number"
                    value={editData.drillFeet}
                    onChange={(e) => setEditData({ ...editData, drillFeet: parseFloat(e.target.value) || 0 })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drill Rate (₹)</label>
                  <input
                    type="number"
                    value={editData.drillRate}
                    onChange={(e) => setEditData({ ...editData, drillRate: parseFloat(e.target.value) || 0 })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drill Amount</label>
                  <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                    ₹{calculateTotalDrillAmount().toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Items */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Additional Items</label>
                <button
                  onClick={addNewItem}
                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Add Item
                </button>
              </div>

              <div className="space-y-2">
                {editData.additional.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center p-2 border border-gray-200 rounded">
                    <input
                      type="text"
                      placeholder="Item name"
                      value={item.name || ''}
                      onChange={(e) => handleAdditionalChange(index, 'name', e.target.value)}
                      className="flex-1 p-1 border border-gray-300 rounded text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity || ''}
                      onChange={(e) => handleAdditionalChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20 p-1 border border-gray-300 rounded text-sm"
                    />
                    <input
                      type="number"
                      placeholder="Rate"
                      value={item.rate || ''}
                      onChange={(e) => handleAdditionalChange(index, 'rate', parseFloat(e.target.value) || 0)}
                      className="w-24 p-1 border border-gray-300 rounded text-sm"
                    />
                    <div className="w-24 text-sm font-medium">
                      ₹{item.total || 0}
                    </div>
                    <button
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Summary */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-medium text-green-800">Total Bill Amount:</span>
                <span className="text-xl font-bold text-green-900">₹{calculateTotalBillAmount().toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Delete Confirmation Modal
  const DeleteModal = ({ bill, onClose, onConfirm }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center mb-4">
          <div className="bg-red-100 p-2 rounded-full mr-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Delete Bill</h3>
        </div>

        <p className="text-gray-600 mb-6">
          Are you sure you want to delete the bill for "{bill.clientName}"? This action cannot be undone.
        </p>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(bill._id)}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const PaymentModal = ({ bill, onClose, onSave }) => {
      const [paymentData, setPaymentData] = useState({
        paymentMethod: 'cash',
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        notes: ''
      });

      const handleSubmit = () => {
        if (!paymentData.amount || paymentData.amount <= 0) {
          alert('Please enter a valid payment amount');
          return;
        }

        onSave(bill._id, {
          ...paymentData,
          amount: parseFloat(paymentData.amount)
        });
      };

      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Payment</h3>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Bill for: <span className="font-medium">{bill.clientName}</span></p>
              <p className="text-sm text-gray-600">Total Bill: <span className="font-medium text-green-600">₹{bill.totalBillAmount?.toLocaleString()}</span></p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={paymentData.paymentMethod}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="card">Card</option>
                  <option value="online">Online</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400"
                  placeholder="Enter payment amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentData.paymentDate}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400"
                  rows="2"
                  placeholder="Payment notes..."
                  maxLength="500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Add Payment
              </button>
            </div>
          </div>
        </div>
      );
    };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading bills...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <div className="flex">
          <div className="text-red-600">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-2">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={fetchBills}
              className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 bg-white">
      {/* Header */}
      <div className="mb-6 mx-2">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6 text-gray-700"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10,9 9,9 8,9" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900">Bills Management</h2>
          </div>

          <button
            onClick={fetchBills}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors duration-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M8 16H3v5" />
            </svg>
            Refresh
          </button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm text-gray-600">
            Total bills: {bills.length} | Filtered: {filteredBills.length}
          </p>

          {/* Summary Card */}
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg px-4 py-2">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              <div>
                <p className="text-sm font-medium text-green-800">Total Amount</p>
                <p className="text-lg font-bold text-green-900">
                  ₹{totalBillAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 mx-2 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by client name..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <div className="sm:w-48">
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateFilter}
            className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <button
          onClick={clearFilters}
          className="px-4 py-2.5 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 whitespace-nowrap"
        >
          Clear Filters
        </button>
      </div>

      {/* Bills Display */}
      {filteredBills.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>No bills found matching your criteria.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedBills).map(([date, dateBills]) => (
            <div key={date} className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {formatDateOnly(date)} ({dateBills.length} {dateBills.length === 1 ? 'bill' : 'bills'})
              </h3>

              <div className="grid gap-4">
                {dateBills.map((bill) => (
                  <div key={bill._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                      <div className="flex items-center gap-3 mb-2 lg:mb-0">
                        <div className="bg-blue-100 p-2 rounded-full">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">
                            {bill.clientName || 'No Client Name'}
                          </h4>
                          <p className="text-sm text-gray-500">
                            Created: {formatDate(bill.createdAt)}
                          </p>
                          <div className="flex gap-4 mt-1">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {bill.drillFeet} ft @ ₹{bill.drillRate}/ft
                            </span>
                            {bill.additional && bill.additional.length > 0 && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                +{bill.additional.length} additional items
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">
                            ₹{(bill.totalBillAmount || 0).toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            Drill: ₹{(bill.totalDrillAmount || 0).toLocaleString()}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => setPaymentModal(bill)}
                            className="p-2 bg-green-100 text-green-600 rounded-md hover:bg-green-200 transition-colors"
                            title="Add payment"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                          </button>

                          <button
                            onClick={() => setEditingBill(bill)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 transition-colors"
                            title="Edit bill"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          <button
                            onClick={() => setDeleteConfirm(bill)}
                            className="p-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                            title="Delete bill"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Drilling Details */}
                    <div className="bg-blue-50 p-3 rounded-md mb-3">
                      <h5 className="text-sm font-medium text-blue-800 mb-2">Drilling Details</h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-blue-600">Feet:</span>
                          <span className="ml-2 font-medium">{bill.drillFeet}</span>
                        </div>
                        <div>
                          <span className="text-blue-600">Rate:</span>
                          <span className="ml-2 font-medium">₹{bill.drillRate}</span>
                        </div>
                        <div>
                          <span className="text-blue-600">Amount:</span>
                          <span className="ml-2 font-medium">₹{(bill.totalDrillAmount || 0).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Additional Items */}
                    {bill.additional && bill.additional.length > 0 ? (
                      <div className="overflow-x-auto">
                        <h5 className="text-sm font-medium text-gray-700 mb-2">Additional Items</h5>
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Item Name
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Quantity
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rate
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {bill.additional.map((item) => (
                              <tr key={item._id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                  {item.name}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500">
                                  {item.quantity}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500">
                                  ₹{item.rate.toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-green-600">
                                  ₹{item.total.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>

                          
                        </table>

                        <table className="min-w-full mt-5">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Payment Id
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Mode
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Note
                              </th>
                            </tr>
                          </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {bill.Payment.map((item) => (
                              <tr key={item._id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm font-medium text-gray-900">
                                  {item.billId}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500">
                                  {item.paymentMethod}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500">
                                  ₹{item.amount}
                                </td>
                                <td className="px-4 py-2 text-sm font-medium text-green-600">
                                  {item?.notes?.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          </table>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
                        <svg className="h-8 w-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                        <p className="text-sm">No additional items</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {editingBill && (
        <EditModal
          bill={editingBill}
          onClose={() => setEditingBill(null)}
          onSave={handleUpdateBill}
        />
      )}

      {deleteConfirm && (
        <DeleteModal
          bill={deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={handleDeleteBill}
        />
      )}
      {paymentModal && (
        <PaymentModal
          bill={paymentModal}
          onClose={() => setPaymentModal(null)}
          onSave={handleAddPayment}
        />
      )}
    </div>
  );
};

export default BillsTable;